import { useQuery } from '@apollo/client'
import { useTheme } from '@emotion/react'
import { Stack, TextField, Typography } from '@mui/material'
import { GetEppCategories } from 'recruiter/graphql/candidateProfile.gql'
import { getPxAsRem } from 'shared/utils'

export interface EppFormValues {
    categoryValues: Map<string, number | null>
}

export interface EppErrorFormValues {
    categoryErrors: Map<string, string>
}

export interface EppScoresProps {
    isEditing: boolean
    onScoreUpdate: (newValues: EppFormValues) => void
    updateErrorValues: (newValues: EppErrorFormValues) => void
    errors: EppErrorFormValues
    formValues: EppFormValues
}

const EppScores: React.FC<EppScoresProps> = ({
    isEditing,
    onScoreUpdate,
    updateErrorValues,
    errors,
    formValues,
}) => {
    const theme = useTheme()

    const { data: eppCategories } = useQuery(GetEppCategories, {
        onCompleted: (res) => {
            const newForm = formValues
            res.eppCategories.forEach((eppCat) => {
                if (!newForm.categoryValues.has(eppCat.id)) {
                    newForm.categoryValues.set(eppCat.id, null)
                }
            })
            onScoreUpdate({
                ...newForm,
            })
        },
    })
    const numColumns = 3
    const numRows = Math.max(
        Math.ceil((eppCategories?.eppCategories.length ?? 0) / numColumns),
        1
    )
    const columns = [[], [], []] as Array<Array<{ id: string; value: string }>>

    eppCategories?.eppCategories.forEach((category, index) => {
        const columnIndex = Math.floor(index / numRows)
        columns[columnIndex].push(category)
    })
    return (
        <>
            <Typography variant="body2">EPP</Typography>
            <Stack
                direction="row"
                spacing={2}
                sx={{ paddingRight: isEditing ? getPxAsRem(25) : 0 }}
            >
                {columns.map((column, columnIndex) => (
                    <Stack
                        key={columnIndex}
                        direction="column"
                        spacing={isEditing ? 2 : 1}
                        sx={{
                            flexGrow: 1,
                        }}
                    >
                        {column.map((category) =>
                            isEditing ? (
                                <TextField
                                    key={category.id}
                                    type="number"
                                    fullWidth
                                    inputProps={{
                                        type: 'number',
                                        min: 0,
                                        max: 100,
                                        step: 1,
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    size="medium"
                                    label={category.value}
                                    name={category.value}
                                    value={
                                        formValues.categoryValues.get(
                                            category.id
                                        ) ?? ''
                                    }
                                    error={
                                        errors.categoryErrors.get(
                                            category.id
                                        ) !== ''
                                    }
                                    onChange={(e) => {
                                        const newValue = +e.target.value
                                        const newForm = formValues
                                        newForm.categoryValues.set(
                                            category.id,
                                            newValue
                                        )

                                        onScoreUpdate({
                                            ...newForm,
                                        })
                                        if (
                                            e.target.value &&
                                            (newValue < 0 || newValue > 100)
                                        ) {
                                            const newErrors = errors
                                            newErrors.categoryErrors.set(
                                                category.id,
                                                'Score must be between 0 and 100'
                                            )
                                            updateErrorValues({
                                                ...newErrors,
                                            })
                                            return
                                        }
                                        const newErrors = errors
                                        newErrors.categoryErrors.set(
                                            category.id,
                                            ''
                                        )
                                        updateErrorValues({
                                            ...newErrors,
                                        })
                                    }}
                                />
                            ) : (
                                <Typography
                                    key={category.id}
                                    variant="body2"
                                    color={theme.palette.other.secondary_light}
                                >
                                    {`${category.value}: ${formValues.categoryValues.get(category.id) ?? '-'}`}
                                </Typography>
                            )
                        )}
                    </Stack>
                ))}
            </Stack>
        </>
    )
}

export default EppScores
